import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../api/api';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../hooks/useAuth';
import { CreditCard, Calendar, Crown, Clock, XCircle, FileText, Shield, ScrollText, ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function MyMembership() {
    const { isDarkMode } = useTheme();
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [membership, setMembership] = useState(null);
    const [loading, setLoading] = useState(true);
    const [subscribing, setSubscribing] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [undoing, setUndoing] = useState(false);

    useEffect(() => {
        // Wait until auth has settled and a user is confirmed before hitting
        // /membership/me — fetching during the post-login auth flap causes a
        // remount/refetch storm.
        if (authLoading) return;
        if (!user) { setLoading(false); return; }
        const fetchMembership = async () => {
            try {
                const response = await api.get('/membership/me');
                setMembership(response.data);
            } catch (err) {
                console.error("No membership found or error fetching", err);
            } finally {
                setLoading(false);
            }
        };
        fetchMembership();
    }, [authLoading, user]);

    if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        <span className={`ml-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Loading membership details...</span>
      </div>
    );
  }

    const handleManageSubscription = async () => {
        setSubscribing(true);
        try {
            const priceId = process.env.REACT_APP_STRIPE_PRICE_ID || "price_12345"; 
            const email = membership?.email || membership?.user_email || "";
            const response = await api.post('/membership/create-checkout-session', {
                price_id: priceId,
                customer_email: email,
            });
            if (response.data.url) {
                window.location.href = response.data.url;
            }
        } catch (err) {
            alert("Could not start subscription process. Please try again.");
        } finally {
            setSubscribing(false);
        }
    };

    const handleCancelMembership = async () => {
        setCancelling(true);
        try {
            await api.post('/membership/cancel');
            setMembership(prev => prev ? { ...prev, CancelledAt: new Date().toISOString() } : prev);
            setShowCancelConfirm(false);
        } catch (err) {
            alert('Failed to cancel membership. Please try again.');
        } finally {
            setCancelling(false);
        }
    };

    // Undo a scheduled downgrade — stay on the current paid plan (no charge).
    const handleCancelDowngrade = async () => {
        setUndoing(true);
        try {
            await api.post('/membership/cancel-downgrade');
            const response = await api.get('/membership/me');
            setMembership(response.data);
        } catch (err) {
            alert(err.response?.data?.error || 'Could not keep your current plan. Please try again.');
        } finally {
            setUndoing(false);
        }
    };

    return (
        <div className={`max-w-2xl mx-auto space-y-6 py-10 px-4 ${isDarkMode ? 'bg-surface' : ''}`}>
            {/* Header */}
            <header className="text-center">
                <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>My Membership</h1>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Manage your subscription and billing</p>
            </header>

            {/* Scheduled-downgrade banner — the member chose a lower tier but keeps
                their paid features until EndDate, and can undo it for free. */}
            {membership?.PendingTierID && (
                <div className={`rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${isDarkMode ? 'bg-slate-800 border-warning/40' : 'bg-warning-tint border-warning/40'}`}>
                    <div className="flex items-start gap-3 flex-1">
                        <Clock className="text-warning shrink-0 mt-0.5" size={18} />
                        <div>
                            <p className={`text-sm font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                Switching to {membership.PendingTierName || 'Free'} on {membership?.EndDate ? new Date(membership.EndDate).toLocaleDateString() : 'your renewal date'}
                            </p>
                            <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                You keep your {membership.TierName} features until then. Switch back anytime before that — no charge.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleCancelDowngrade}
                        disabled={undoing}
                        className="whitespace-nowrap px-4 py-2 rounded-xl text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 transition-colors disabled:opacity-50"
                    >
                        {undoing ? 'Working…' : `Keep ${membership.TierName}`}
                    </button>
                </div>
            )}

            {/* Plan Card */}
            <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200 shadow-sm'}`}>
                {/* Plan Banner */}
                <div className="bg-slate-900 px-6 py-6 text-center relative">
                    <Crown className="text-amber-400 mx-auto mb-2" size={28} />
                    <h2 className="text-xl font-bold text-white">{membership?.TierName || "Free Tier"}</h2>
                    <span className={`inline-block mt-2 px-3 py-0.5 rounded-full text-xs font-bold border ${
                        membership?.CancelledAt
                            ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                            : membership?.isActive 
                                ? 'bg-primary-500/20 text-primary-400 border-primary-500/30' 
                                : 'bg-red-500/20 text-red-400 border-red-500/30'
                    }`}>
                        {membership?.CancelledAt ? 'Cancelled' : membership?.isActive ? 'Active' : 'Inactive'}
                    </span>
                    {membership?.Price && (
                        <p className="mt-3">
                            <span className="text-3xl font-extrabold text-primary-400">${Number(membership.Price).toFixed(2)}</span>
                            <span className="text-sm text-slate-400">/mo</span>
                        </p>
                    )}
                </div>

                {/* Details */}
                <div className="px-6 py-5 space-y-5">
                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className={`rounded-xl p-3 ${isDarkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                            <div className="flex items-center gap-2 mb-1">
                                <Calendar className="text-primary-500" size={14} />
                                <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`}>Started</p>
                            </div>
                            <p className={`text-sm font-bold ${isDarkMode ? 'text-slate-200' : 'text-slate-900'}`}>
                                {membership?.StartDate ? new Date(membership.StartDate).toLocaleDateString() : 'N/A'}
                            </p>
                        </div>
                        <div className={`rounded-xl p-3 ${isDarkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                            <div className="flex items-center gap-2 mb-1">
                                <Clock className="text-primary-500" size={14} />
                                <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`}>{(membership?.CancelledAt || membership?.PendingTierID) ? 'Ends' : 'Renews'}</p>
                            </div>
                            <p className={`text-sm font-bold ${isDarkMode ? 'text-slate-200' : 'text-slate-900'}`}>
                                {membership?.EndDate ? new Date(membership.EndDate).toLocaleDateString() : 'Ongoing'}
                            </p>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="space-y-2">
                        <button
                            className="w-full py-3 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 bg-primary-500 text-white hover:bg-primary-600 shadow-sm"
                            onClick={() => navigate('/membership/plans')}
                            disabled={subscribing}
                        >
                            <ArrowUpRight size={16} /> {membership?.isActive ? 'View Plans' : 'Upgrade Now'}
                        </button>
                        {membership?.isActive && !membership?.CancelledAt && !membership?.PendingTierID && Number(membership?.Price) > 0 && (
                            <button
                                onClick={() => setShowCancelConfirm(true)}
                                className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 border ${
                                    isDarkMode 
                                        ? 'text-red-400 border-red-800/50 hover:bg-red-900/20' 
                                        : 'text-red-500 border-red-200 hover:bg-red-50'
                                }`}
                            >
                                <XCircle size={16} /> Cancel Membership
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Legal Links */}
            <div className={`flex flex-wrap justify-center gap-5 text-xs ${
                isDarkMode ? 'text-slate-500' : 'text-slate-400'
            }`}>
                <a href="/membership-agreement" className="flex items-center gap-1 hover:text-slate-600 transition-colors">
                    <ScrollText size={12} /> Membership Agreement
                </a>
                <a href="/terms-of-service" className="flex items-center gap-1 hover:text-slate-600 transition-colors">
                    <FileText size={12} /> Terms of Service
                </a>
                <a href="/privacy-policy" className="flex items-center gap-1 hover:text-slate-600 transition-colors">
                    <Shield size={12} /> Privacy Policy
                </a>
            </div>

            {/* Cancel Confirmation Modal */}
            {showCancelConfirm && createPortal(
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm" style={{ zIndex: 9999 }} onClick={() => setShowCancelConfirm(false)}>
                    <div className={`rounded-2xl shadow-xl max-w-md w-full mx-4 p-6 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-red-100 rounded-full"><XCircle className="text-red-500" size={24} /></div>
                            <h3 className={`text-lg font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Cancel Membership</h3>
                        </div>
                        <p className={`text-sm mb-6 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            Are you sure you want to cancel your membership? Your subscription will remain active until the end of your current billing period.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowCancelConfirm(false)}
                                className={`flex-1 py-3 rounded-xl font-semibold border transition-colors ${
                                    isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                                }`}
                            >
                                Keep Membership
                            </button>
                            <button
                                onClick={handleCancelMembership}
                                disabled={cancelling}
                                className="flex-1 py-3 rounded-xl font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
                            >
                                {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}